import { useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { api, formatApiError } from "../lib/api";
import { Loader2, CheckCircle2, XCircle, FlaskConical, Lock } from "lucide-react";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export const RegistrationModal = ({ open, onOpenChange, config }) => {
  const [step, setStep] = useState("form");
  const [form, setForm] = useState({ full_name: "", email: "", consent: false });
  const [reg, setReg] = useState(null);
  const [error, setError] = useState("");

  const reset = () => {
    setStep("form");
    setError("");
  };

  const handleOpenChange = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(reset, 300);
  };

  const verifyOnServer = async (resp) => {
    setStep("processing");
    try {
      const { data } = await api.post("/payments/verify", {
        registration_id: reg.registration_id,
        razorpay_order_id: resp.razorpay_order_id,
        razorpay_payment_id: resp.razorpay_payment_id,
        razorpay_signature: resp.razorpay_signature,
      });
      setStep(data.payment_status === "paid" ? "success" : "failed");
    } catch (e) {
      setError(formatApiError(e, "Payment verification failed."));
      setStep("failed");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent) {
      setError("Please tick the consent checkbox to continue.");
      return;
    }
    setStep("processing");
    try {
      const { data } = await api.post("/registrations", form);
      setReg(data);
      if (data.demo) {
        setStep("demo");
        return;
      }
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        setError("Could not load the payment gateway. Please check your connection.");
        setStep("failed");
        return;
      }
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: config?.workshop_title || "Soma & Soul",
        description: config?.workshop_subtitle || "Workshop registration",
        order_id: data.order_id,
        prefill: { name: form.full_name, email: form.email },
        theme: { color: "#5E7D6A" },
        handler: verifyOnServer,
        modal: { ondismiss: () => setStep("form") },
      });
      rzp.open();
      setStep("awaiting");
    } catch (e2) {
      setError(formatApiError(e2));
      setStep("form");
    }
  };

  const demoComplete = async (outcome) => {
    setStep("processing");
    try {
      const { data } = await api.post("/payments/demo-complete", {
        registration_id: reg.registration_id,
        outcome,
      });
      setStep(data.payment_status === "paid" ? "success" : "failed");
      if (data.payment_status !== "paid") setError("The demo payment was marked as failed.");
    } catch (e) {
      setError(formatApiError(e));
      setStep("failed");
    }
  };

  const price = config?.price_inr ? `₹${config.price_inr.toLocaleString("en-IN")}` : "";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-md rounded-[26px] border-[#D9C3A3]/60 bg-[#FAF7F2] p-8"
        data-testid="registration-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-light text-[#262320]">
            {step === "success" ? "Your spot is reserved" : "Reserve Your Spot"}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#6E6862]">
            {step === "form" && `${config?.workshop_title || "Soma & Soul"} · ${config?.date || ""} · ${price}`}
            {step === "processing" && "One moment, please..."}
            {step === "demo" && "Payments are running in test mode"}
            {step === "awaiting" && "Complete your payment in the Razorpay window"}
            {step === "success" && "We have saved your seat in the circle"}
            {step === "failed" && "Your payment could not be completed"}
          </DialogDescription>
        </DialogHeader>

        {step === "form" && (
          <form onSubmit={submit} className="mt-2 space-y-5">
            <div>
              <label htmlFor="reg-name" className="mb-2 block text-xs uppercase tracking-[0.15em] text-[#5E7D6A]">
                Full name
              </label>
              <input
                id="reg-name"
                data-testid="input-full-name"
                type="text"
                required
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                placeholder="Your full name"
                className="w-full rounded-xl border border-[#D9C3A3]/70 bg-white px-4 py-3 text-sm text-[#262320] outline-none placeholder:text-[#6E6862]/60 focus:border-[#5E7D6A]"
              />
            </div>
            <div>
              <label htmlFor="reg-email" className="mb-2 block text-xs uppercase tracking-[0.15em] text-[#5E7D6A]">
                Email address
              </label>
              <input
                id="reg-email"
                data-testid="input-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com"
                className="w-full rounded-xl border border-[#D9C3A3]/70 bg-white px-4 py-3 text-sm text-[#262320] outline-none placeholder:text-[#6E6862]/60 focus:border-[#5E7D6A]"
              />
            </div>
            <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-[#6E6862]">
              <Checkbox
                data-testid="checkbox-consent"
                checked={form.consent}
                onCheckedChange={(v) => setForm({ ...form, consent: v === true })}
                className="mt-0.5 border-[#5E7D6A] data-[state=checked]:bg-[#5E7D6A]"
              />
              <span>
                I agree to receive workshop-related emails, including my confirmation and Zoom joining link. See our{" "}
                <Link to="/privacy" target="_blank" className="text-[#5E7D6A] underline underline-offset-2">
                  privacy policy
                </Link>
                .
              </span>
            </label>
            {error && (
              <p className="rounded-xl bg-[#F4E3E0] px-4 py-3 text-xs text-[#262320]" data-testid="form-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="modal-submit-button"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#5E7D6A] py-4 text-sm font-medium text-[#FAF7F2] transition-colors hover:bg-[#4d6a59]"
            >
              <Lock size={14} />
              Continue to secure payment {price && `· ${price}`}
            </button>
            <p className="text-center text-[11px] text-[#6E6862]">Secure checkout powered by Razorpay</p>
          </form>
        )}

        {step === "processing" && (
          <div className="flex flex-col items-center gap-4 py-10" data-testid="payment-processing">
            <Loader2 size={32} className="animate-spin text-[#5E7D6A]" />
            <p className="text-sm text-[#6E6862]">Talking to the payment gateway...</p>
          </div>
        )}

        {step === "awaiting" && (
          <div className="flex flex-col items-center gap-4 py-8" data-testid="payment-awaiting">
            <Loader2 size={28} className="animate-spin text-[#5E7D6A]" />
            <p className="text-center text-sm text-[#6E6862]">
              The Razorpay window is open. Once you pay, we verify it on our server before confirming your seat.
            </p>
            <button
              data-testid="razorpay-pay-button"
              onClick={submit}
              className="rounded-full border border-[#5E7D6A] px-6 py-2.5 text-xs text-[#5E7D6A] hover:bg-[#5E7D6A] hover:text-white"
            >
              Reopen payment window
            </button>
          </div>
        )}

        {step === "demo" && (
          <div className="space-y-4 py-2" data-testid="demo-payment-panel">
            <div className="flex items-start gap-3 rounded-2xl border border-dashed border-[#D9C3A3] bg-[#F5F0E8] p-4">
              <FlaskConical size={18} className="mt-0.5 shrink-0 text-[#5E7D6A]" />
              <p className="text-xs leading-relaxed text-[#6E6862]">
                <strong className="text-[#262320]">Test mode:</strong> Razorpay keys are not configured yet, so no real
                money moves. Simulate an outcome below — the server still verifies and emails exactly as it will in
                production.
              </p>
            </div>
            <button
              data-testid="razorpay-simulate-success"
              onClick={() => demoComplete("success")}
              className="w-full rounded-full bg-[#5E7D6A] py-3.5 text-sm font-medium text-[#FAF7F2] hover:bg-[#4d6a59]"
            >
              Simulate successful payment {price && `· ${price}`}
            </button>
            <button
              data-testid="razorpay-simulate-failure"
              onClick={() => demoComplete("failure")}
              className="w-full rounded-full border border-[#262320]/20 py-3.5 text-sm text-[#262320] hover:bg-white"
            >
              Simulate failed payment
            </button>
          </div>
        )}

        {step === "success" && (
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            <span
              data-testid="payment-success-badge"
              className="flex h-16 w-16 items-center justify-center rounded-full bg-[#D6E0D8]"
            >
              <CheckCircle2 size={32} className="text-[#5E7D6A]" />
            </span>
            <p className="font-serif text-xl text-[#262320]">Welcome to the circle, {form.full_name.split(" ")[0]}.</p>
            <p className="max-w-xs text-sm leading-relaxed text-[#6E6862]">
              Your payment is verified and a confirmation email with the Zoom joining link is on its way to{" "}
              <strong className="text-[#262320]">{form.email}</strong>.
            </p>
            <button
              onClick={() => handleOpenChange(false)}
              className="mt-2 rounded-full border border-[#5E7D6A] px-8 py-3 text-sm text-[#5E7D6A] hover:bg-[#5E7D6A] hover:text-white"
              data-testid="success-close-button"
            >
              Done
            </button>
          </div>
        )}

        {step === "failed" && (
          <div className="flex flex-col items-center gap-4 py-6 text-center" data-testid="payment-failed-state">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#F4E3E0]">
              <XCircle size={32} className="text-[#b06a5e]" />
            </span>
            <p className="font-serif text-xl text-[#262320]">Payment not completed</p>
            <p className="max-w-xs text-sm leading-relaxed text-[#6E6862]">
              {error || "Nothing was charged. You can try again whenever you are ready."} No Zoom link is sent until a
              payment is verified.
            </p>
            <button
              onClick={reset}
              className="mt-2 rounded-full bg-[#5E7D6A] px-8 py-3 text-sm text-[#FAF7F2] hover:bg-[#4d6a59]"
              data-testid="retry-payment-button"
            >
              Try again
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
